<template>
  <div class="org-page">

    <div class="flash is-ok" v-if="flash">
      <span>{{ flash }}</span>
      <button class="flash-close" @click="flash = ''">&times;</button>
    </div>
    <div class="flash is-err" v-if="errorMessage">
      <span>{{ errorMessage }}</span>
      <button class="flash-close" @click="errorMessage = ''">&times;</button>
    </div>

    <div class="skeleton" v-if="loading">{{ $t('common.loading') }}</div>

    <template v-else-if="organization">

      <!-- Kurum banner -->
      <section class="identity">
        <div class="identity-main">
          <div class="org-mark">{{ orgInitials }}</div>

          <div class="identity-text">
            <h2 class="identity-name">{{ organization.name }}</h2>
            <p class="identity-sub">
              <code>{{ organization.slug }}</code>
              <span class="sep">·</span>
              {{ $t('organization.memberCount', { count: members.length }) }}
            </p>
            <div class="chips">
              <span class="chip" v-if="organization.legalName">{{ organization.legalName }}</span>
              <span class="chip" v-if="pendingCount">
                <i class="fa fa-envelope-o"></i>
                {{ pendingCount }} {{ $t('organization.invPending').toLowerCase() }}
              </span>
            </div>
          </div>

          <button class="btn-on-dark" v-if="canManage" @click="openInvite">
            <span class="icon"><i class="fa fa-user-plus"></i></span>
            <span>{{ $t('organization.invite') }}</span>
          </button>
        </div>
      </section>

      <!-- Kurum bilgileri -->
      <section class="card">
        <div class="card-head">
          <h3 class="card-title">{{ $t('organization.info') }}</h3>
          <button class="btn-ghost is-mini" v-if="canManage && !editing" @click="startEdit">
            <span class="icon"><i class="fa fa-pencil"></i></span>
            <span>{{ $t('common.edit') }}</span>
          </button>
        </div>

        <dl class="facts" v-if="!editing">
          <div class="fact">
            <dt>{{ $t('organization.name') }}</dt>
            <dd>{{ organization.name }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('organization.legalName') }}</dt>
            <dd>{{ organization.legalName || '—' }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('organization.taxNumber') }}</dt>
            <dd>{{ organization.taxNumber || '—' }}</dd>
          </div>
          <div class="fact">
            <dt>{{ $t('organization.phone') }}</dt>
            <dd>{{ organization.phone || '—' }}</dd>
          </div>
          <div class="fact is-wide">
            <dt>{{ $t('organization.address') }}</dt>
            <dd class="is-pre">{{ organization.address || '—' }}</dd>
          </div>
          <div class="fact is-wide">
            <dt>{{ $t('organization.locale') }}</dt>
            <dd>{{ localeLabel(organization.locale) }} · {{ organization.timezone }}</dd>
          </div>
        </dl>

        <div class="form" v-else>
          <div class="field">
            <label>{{ $t('organization.name') }}</label>
            <input class="input" v-model.trim="form.name" maxlength="150">
          </div>

          <div class="form-row">
            <div class="field">
              <label>{{ $t('organization.legalName') }}</label>
              <input class="input" v-model.trim="form.legalName" maxlength="150">
            </div>
            <div class="field">
              <label>{{ $t('organization.taxNumber') }}</label>
              <input class="input" v-model.trim="form.taxNumber" maxlength="20">
            </div>
          </div>

          <div class="form-row">
            <div class="field">
              <label>{{ $t('organization.phone') }}</label>
              <input class="input" v-model.trim="form.phone" maxlength="30">
            </div>
            <div class="field">
              <label>{{ $t('organization.inviteLanguage') }}</label>
              <div class="select-wrap">
                <select class="select-input" v-model="form.locale">
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>
            </div>
          </div>

          <div class="field">
            <label>{{ $t('profile.timezone') }}</label>
            <div class="select-wrap">
              <select class="select-input" v-model="form.timezone">
                <option v-for="zone in timezoneList" :key="zone.code" :value="zone.code">
                  {{ zone.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="field">
            <label>{{ $t('organization.address') }}</label>
            <textarea class="input is-area" rows="3" v-model.trim="form.address" maxlength="500"></textarea>
          </div>

          <p class="hint">{{ $t('organization.slugLocked', { slug: organization.slug }) }}</p>

          <div class="actions">
            <button class="btn-primary" :class="{ 'is-loading': saving }" @click="saveOrganization">
              {{ $t('common.save') }}
            </button>
            <button class="btn-ghost" @click="editing = false">{{ $t('common.cancel') }}</button>
          </div>
        </div>
      </section>

      <!-- Üyeler -->
      <section class="card">
        <div class="card-head">
          <h3 class="card-title">{{ $t('organization.members') }}</h3>
          <span class="count">{{ members.length }}</span>
        </div>

        <!-- Tablo yerine satır listesi: dar ekranda tablo yatay kayıyor,
             satırlar ise alt alta yığılıp okunur kalıyor -->
        <ul class="rows">
          <li class="row" v-for="m in members" :key="m._id">
            <span class="row-mark">{{ personInitials(m.fullName || m.email) }}</span>
            <div class="row-text">
              <strong>{{ m.fullName }}</strong>
              <span class="row-sub">{{ m.email }}</span>
              <span class="row-sub" v-if="m.title">{{ m.title }}</span>
            </div>
            <div class="row-side">
              <span class="tagline">{{ roleLabel(m.role) }}</span>
              <span class="pill" :class="m.status === 'ACTV' ? 'is-ok' : 'is-idle'">
                {{ statusLabel(m.status) }}
              </span>
            </div>
          </li>
        </ul>
      </section>

      <!-- Davetler -->
      <section class="card" v-if="canManage">
        <div class="card-head">
          <h3 class="card-title">{{ $t('organization.invitations') }}</h3>
          <button class="btn-ghost is-mini" @click="openInvite">
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t('organization.invite') }}</span>
          </button>
        </div>

        <p class="empty" v-if="!invitations.length">{{ $t('organization.noInvitations') }}</p>

        <ul class="rows" v-else>
          <li class="row" v-for="i in invitations" :key="i._id">
            <span class="row-mark is-soft"><i class="fa fa-envelope-o"></i></span>
            <div class="row-text">
              <strong>{{ i.name }}</strong>
              <span class="row-sub">{{ i.email }}</span>
              <span class="row-sub">
                {{ localeLabel(i.locale) }}
                <span class="sep">·</span>
                {{ $t('organization.validity') }}: {{ formatDate(i.expiresAt) }}
                <span class="warn" v-if="i.status === 'pending' && !i.mailSent">
                  · {{ $t('organization.mailNotSent') }}
                </span>
              </span>
            </div>
            <div class="row-side">
              <span class="pill" :class="invitationPill(i)">{{ invitationLabel(i) }}</span>
              <button class="btn-danger is-mini" v-if="i.status === 'pending'" @click="revoke(i)">
                {{ $t('envelopes.voidAction') }}
              </button>
            </div>
          </li>
        </ul>
      </section>
      <!-- API anahtarları: görüntüleme herkese açık -->
      <section class="card">
        <div class="card-head">
          <h3 class="card-title">{{ $t('organization.apiTitle') }}</h3>
          <button class="btn-ghost is-mini" v-if="canManage" @click="openApiKey">
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t('organization.apiNew') }}</span>
          </button>
        </div>

        <p class="hint">
          {{ $t('organization.apiLead') }}
          <span class="warn-inline" v-if="apiKeysExposed">{{ $t('organization.apiKeyVisible') }}</span>
        </p>

        <!-- Uç adresi ve hesap kimliği: entegrasyonu yazan kişi ikisini de
             arıyor, anahtarla birlikte burada dursun -->
        <dl class="facts">
          <div class="fact is-wide">
            <dt>{{ $t('organization.apiEndpoint') }}</dt>
            <dd class="copy-line">
              <code>POST {{ apiEndpoint }}</code>
              <button class="copy-btn" @click="copyText(apiEndpoint)">
                <i class="fa fa-clipboard"></i>
              </button>
            </dd>
          </div>
          <div class="fact is-wide">
            <dt>{{ $t('organization.apiAccountId') }}</dt>
            <dd class="copy-line">
              <code>{{ organization._id }}</code>
              <button class="copy-btn" :title="$t('organization.copyId')"
                      @click="copyText(organization._id)">
                <i class="fa fa-clipboard"></i>
              </button>
            </dd>
          </div>
        </dl>

        <p class="empty" v-if="!apiKeys.length">{{ $t('organization.apiNone') }}</p>

        <ul class="rows" v-else>
          <li class="row" v-for="k in apiKeys" :key="k._id">
            <span class="row-mark is-soft"><i class="fa fa-key"></i></span>

            <div class="row-text">
              <strong>{{ k.name }}</strong>
              <span class="row-sub copy-line">
                <code>{{ k.token || (k.prefix + '…') }}</code>
                <button class="copy-btn" v-if="k.token" :title="$t('organization.copyKey')"
                        @click.stop="copyText(k.token)">
                  <i class="fa fa-clipboard"></i>
                </button>
              </span>
              <span class="row-sub">
                {{ $t('organization.apiUsage') }}: {{ k.usageCount || 0 }}
                <span class="sep">·</span>
                {{ $t('organization.apiLastUsed') }}:
                {{ k.lastUsedAt ? formatDate(k.lastUsedAt) : $t('organization.apiNever') }}
                <span class="sep">·</span>
                {{ $t('organization.apiValidity') }}: {{ formatDate(k.expiresAt) }}
              </span>
            </div>

            <div class="row-side">
              <span class="pill" :class="apiKeyPill(k)">{{ apiKeyLabel(k) }}</span>
              <button class="btn-danger is-mini" v-if="!k.revokedAt && new Date(k.expiresAt) > new Date()"
                      @click="revokeApiKey(k)">
                {{ $t('organization.apiRevoke') }}
              </button>
            </div>
          </li>
        </ul>
      </section>
    </template>

    <!-- API anahtarı penceresi -->
    <div class="sheet" v-if="apiOpen">
      <div class="sheet-backdrop" @click="apiOpen = false"></div>

      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon"><i class="fa fa-key"></i></span>
          <div class="sheet-head-text">
            <h3>{{ newToken ? $t('organization.apiCreated') : $t('organization.apiNew') }}</h3>
            <p>{{ newToken ? $t('organization.apiOnceWarning') : $t('organization.apiLead') }}</p>
          </div>
          <button class="sheet-close" @click="apiOpen = false" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">
          <div class="flash is-err" v-if="apiError">
            <span>{{ apiError }}</span>
            <button class="flash-close" @click="apiError = ''">&times;</button>
          </div>

          <!-- Üretildikten sonra -->
          <template v-if="newToken">
            <div class="link-row">
              <span class="link-text">{{ newToken }}</span>
              <button class="btn-ghost is-mini" @click="copyToken">
                <span class="icon"><i class="fa" :class="copied ? 'fa-check' : 'fa-clipboard'"></i></span>
                <span>{{ copied ? $t('organization.copied') : $t('organization.copyKey') }}</span>
              </button>
            </div>

            <dl class="facts mt">
              <div class="fact is-wide">
                <dt>{{ $t('organization.apiAccountId') }}</dt>
                <dd><code>{{ organization._id }}</code></dd>
              </div>
            </dl>
          </template>

          <!-- Form -->
          <div class="form" v-else>
            <div class="field">
              <label>{{ $t('organization.apiName') }} <em>*</em></label>
              <input class="input" v-model.trim="apiForm.name"
                     :placeholder="$t('organization.apiNamePlaceholder')"
                     ref="apiName" @keyup.enter="createApiKey">
            </div>

            <div class="field">
              <label>{{ $t('organization.apiValidity') }}</label>
              <div class="seg">
                <button v-for="m in [12, 24, 60]" :key="m"
                        :class="{ 'is-active': apiForm.months === m }"
                        @click="apiForm.months = m">
                  {{ $t('organization.apiMonths', { count: m }) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <footer class="sheet-foot">
          <button class="btn-primary" v-if="!newToken"
                  :class="{ 'is-loading': saving }" @click="createApiKey">
            {{ $t('common.create') }}
          </button>
          <button class="btn-ghost" @click="apiOpen = false">
            {{ newToken ? $t('common.close') : $t('common.cancel') }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Davet penceresi -->
    <div class="sheet" v-if="inviteOpen">
      <div class="sheet-backdrop" @click="inviteOpen = false"></div>

      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon"><i class="fa fa-user-plus"></i></span>
          <div class="sheet-head-text">
            <h3>{{ lastInviteUrl ? $t('organization.inviteSentTitle') : $t('organization.inviteTitle') }}</h3>
            <p>{{ lastInviteUrl ? $t('organization.inviteLinkHint') : $t('organization.inviteLead') }}</p>
          </div>
          <button class="sheet-close" @click="inviteOpen = false" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">
          <div class="flash is-err" v-if="inviteError">
            <span>{{ inviteError }}</span>
            <button class="flash-close" @click="inviteError = ''">&times;</button>
          </div>

          <!-- Gönderim sonrası -->
          <div class="sent-box" v-if="lastInviteUrl">
            <p class="sent-title">{{ $t('organization.inviteCreated', { name: invite.name }) }}</p>

            <div class="link-row">
              <span class="link-text">{{ lastInviteUrl }}</span>
              <button class="btn-ghost is-mini" @click="copyLink">
                <span class="icon"><i class="fa" :class="copied ? 'fa-check' : 'fa-clipboard'"></i></span>
                <span>{{ copied ? $t('organization.copied') : $t('organization.copyLink') }}</span>
              </button>
            </div>
          </div>

          <!-- Form -->
          <div class="form" v-else>
            <div class="field">
              <label>{{ $t('organization.inviteName') }} <em>*</em></label>
              <input class="input" v-model.trim="invite.name" placeholder="Jane Doe" ref="inviteName">
            </div>

            <div class="field" :class="{ 'has-error': inviteConflict }">
              <label>{{ $t('organization.inviteEmail') }} <em>*</em></label>
              <input class="input" type="email" v-model.trim="invite.email"
                     placeholder="name@example.com" @keyup.enter="sendInvite">
              <!-- Çakışma yazarken görünüyor: gönder'e basıp hata almak
                   yerine sorun anında belli oluyor -->
              <p class="err" v-if="inviteConflict">
                <i class="fa fa-exclamation-circle"></i> {{ inviteConflict }}
              </p>
            </div>

            <!-- Rol: üç seçenek için açılır liste fazla; segment daha hızlı -->
            <div class="field">
              <label>{{ $t('organization.inviteRole') }}</label>
              <div class="seg">
                <button v-for="option in roleOptions" :key="option.value"
                        :class="{ 'is-active': invite.role === option.value }"
                        @click="invite.role = option.value">
                  {{ $t('profile.' + option.labelKey) }}
                </button>
              </div>
            </div>

            <div class="field">
              <label>{{ $t('organization.inviteLanguage') }}</label>
              <div class="seg">
                <button v-for="code in $locales" :key="code"
                        :class="{ 'is-active': invite.locale === code }"
                        @click="invite.locale = code">
                  {{ languageName(code) }}
                </button>
              </div>
              <p class="hint">{{ $t('organization.inviteLangHelp') }}</p>
            </div>
          </div>
        </div>

        <footer class="sheet-foot">
          <button class="btn-primary" v-if="!lastInviteUrl"
                  :class="{ 'is-loading': saving }"
                  :disabled="!!inviteConflict" @click="sendInvite">
            <span class="icon"><i class="fa fa-paper-plane-o"></i></span>
            <span>{{ $t('organization.sendInvite') }}</span>
          </button>
          <button class="btn-ghost" @click="inviteOpen = false">
            {{ lastInviteUrl ? $t('common.close') : $t('common.cancel') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../config/api';
import geo from '../config/geo';
import Defaults from '../config/Defaults';

export default {
  name: 'Organization',
  data() {
    return {
      organization: null,
      members: [],
      invitations: [],

      loading: true,
      saving: false,
      editing: false,
      flash: '',
      errorMessage: '',

      form: {},
      inviteOpen: false,
      inviteError: '',
      lastInviteUrl: '',
      invite: { name: '', email: '', locale: 'tr', role: 'member' },
      copied: false,

      // API anahtarları
      apiKeys: [],
      // Sunucu test ortamındaysa anahtarların açık hali geliyor
      apiKeysExposed: false,
      apiOpen: false,
      apiError: '',
      newToken: '',
      apiForm: { name: '', months: 24 },

      roleOptions: [
        { value: 'member', labelKey: 'roleMember' },
        { value: 'admin', labelKey: 'roleAdmin' },
        { value: 'viewer', labelKey: 'roleViewer' }
      ]
    };
  },
  computed: {
    timezoneList() {
      return geo.timezones(this.$locale, this.form.timezone
        || (this.organization && this.organization.timezone));
    },
    // Entegrasyonu yazan kişinin ihtiyaç duyduğu tam adres
    apiEndpoint() {
      var base = '';
      try {
        base = Defaults.applications().backEnd.url;
      } catch (e) { /* yapılandırma okunamazsa göreli adres */ }
      return base + '/restapi/v2.1/accounts/'
        + (this.organization ? this.organization._id : '{accountId}') + '/envelopes';
    },
    // Girilen adres zaten üye mi ya da bekleyen daveti var mı.
    // Sunucu da aynı kontrolü yapıyor; buradaki yalnızca kullanıcıyı
    // gönder'e basmadan uyarmak için.
    inviteConflict() {
      var email = String(this.invite.email || '').trim().toLowerCase();
      if (!email) return '';

      var member = this.members.find(function (m) {
        return String(m.email || '').toLowerCase() === email;
      });
      if (member) return this.$t('organization.alreadyMember', { email: email });

      var now = new Date();
      var pending = this.invitations.find(function (i) {
        return String(i.email || '').toLowerCase() === email
          && i.status === 'pending'
          && new Date(i.expiresAt) > now;
      });
      if (pending) return this.$t('organization.alreadyInvited', { email: email });

      return '';
    },

    orgInitials() {
      return this.personInitials(this.organization ? this.organization.name : '');
    },
    // Bekleyen davet sayısı banner'da rozet olarak duruyor; yönetici
    // sayfaya girer girmez bekleyen iş olup olmadığını görüyor
    pendingCount() {
      return this.invitations.filter(function (i) {
        return i.status === 'pending' && new Date(i.expiresAt) > new Date();
      }).length;
    },
    // Rol '4' ve '5' kurum ayarlarını değiştirebiliyor; sunucu da
    // aynı kontrolü yapıyor, buradaki yalnızca arayüzü sadeleştirmek için
    canManage() {
      const role = this.$store && this.$store.state.the_app_data.the_user
        ? this.$store.state.the_app_data.the_user.role
        : null;
      return role === '4' || role === '5';
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      api.get('/organizations/current')
        .then((body) => {
          this.organization = body.organization;
          if (!this.organization) {
            this.errorMessage = this.$t('organization.notInOrg');
            return null;
          }
          return this.loadMembers();
        })
        .then(() => {
          if (!this.organization) return null;
          // Anahtarları kurum üyelerinin hepsi görüyor; davetler yöneticide
          var jobs = [this.loadApiKeys()];
          if (this.canManage) jobs.push(this.loadInvitations());
          return Promise.all(jobs);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.loading = false; });
    },

    loadMembers() {
      return api.get('/organizations/members')
        .then((body) => { this.members = body.memberList; });
    },

    loadInvitations() {
      return api.get('/organizations/invitations')
        .then((body) => { this.invitations = body.invitationList; })
        .catch(() => { /* davetleri görememek sayfayı bozmasın */ });
    },

    loadApiKeys() {
      return api.get('/organizations/api-keys')
        .then((body) => {
          this.apiKeys = body.apiKeyList;
          this.apiKeysExposed = !!body.exposeKeys;
        })
        .catch(() => { /* anahtarları görememek sayfayı bozmasın */ });
    },

    openApiKey() {
      this.apiForm = { name: '', months: 24 };
      this.apiError = '';
      this.newToken = '';
      this.copied = false;
      this.apiOpen = true;
      this.$nextTick(() => {
        if (this.$refs.apiName) this.$refs.apiName.focus();
      });
    },

    createApiKey() {
      if (this.saving) return;
      if (!this.apiForm.name || this.apiForm.name.length < 2) {
        this.apiError = this.$t('organization.apiErrName');
        return;
      }

      this.saving = true;
      this.apiError = '';
      api.post('/organizations/api-keys', this.apiForm)
        .then((body) => {
          // Ham anahtar yalnızca bu cevapta geliyor; kapatınca kayboluyor
          this.newToken = body.token;
          this.notify(body.message);
          this.loadApiKeys();
        })
        .catch((err) => { this.apiError = err.message; })
        .then(() => { this.saving = false; });
    },

    copyToken() {
      this.copyText(this.newToken);
    },

    revokeApiKey(key) {
      api.delete('/organizations/api-keys/' + key._id)
        .then((body) => {
          this.notify(body.message);
          this.loadApiKeys();
        })
        .catch((err) => { this.errorMessage = err.message; });
    },

    apiKeyLabel(key) {
      if (key.revokedAt) return this.$t('organization.apiRevoked');
      if (new Date(key.expiresAt) < new Date()) return this.$t('organization.apiExpired');
      return this.$t('organization.apiActive');
    },
    apiKeyPill(key) {
      if (key.revokedAt) return 'is-dark';
      if (new Date(key.expiresAt) < new Date()) return 'is-warn';
      return 'is-ok';
    },

    startEdit() {
      this.form = {
        name: this.organization.name,
        legalName: this.organization.legalName,
        taxNumber: this.organization.taxNumber,
        phone: this.organization.phone,
        address: this.organization.address,
        locale: this.organization.locale,
        timezone: this.organization.timezone || geo.guessTimezone()
      };
      this.editing = true;
    },

    saveOrganization() {
      if (this.saving) return;
      if (!this.form.name || this.form.name.length < 2) {
        this.errorMessage = this.$t('organization.errName');
        return;
      }
      this.saving = true;
      api.put('/organizations/current', this.form)
        .then((body) => {
          this.organization = body.organization;
          this.editing = false;
          this.notify(body.message);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    openInvite() {
      // Davet dili varsayılan olarak arayüz dili: çoğu zaman aynı ekipten
      // birini davet ediyorsun
      this.invite = { name: '', email: '', locale: this.$locale, role: 'member' };
      this.inviteError = '';
      this.lastInviteUrl = '';
      this.copied = false;
      this.inviteOpen = true;
      this.$nextTick(() => {
        if (this.$refs.inviteName) this.$refs.inviteName.focus();
      });
    },

    copyLink() {
      this.copyText(this.lastInviteUrl);
    },

    // Panoya kopyalar. Clipboard API yoksa (http üzerinde veya eski
    // tarayıcıda) gizli bir input üzerinden eski yöntem.
    copyText(text) {
      const done = () => {
        this.copied = true;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => { this.copied = false; }, 2000);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.copyFallback(text, done));
      } else {
        this.copyFallback(text, done);
      }
    },

    copyFallback(text, done) {
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* kopyalanamadı */ }
      document.body.removeChild(field);
    },

    languageName(code) {
      return { tr: 'Türkçe', en: 'English', fr: 'Français' }[code] || code;
    },

    sendInvite() {
      if (this.saving) return;
      if (!this.invite.name) { this.inviteError = this.$t('organization.errInviteName'); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.invite.email)) {
        this.inviteError = this.$t('organization.errInviteEmail');
        return;
      }
      // Düğme zaten kapalı ama enter ile de gelinebiliyor
      if (this.inviteConflict) {
        this.inviteError = this.inviteConflict;
        return;
      }

      this.saving = true;
      this.inviteError = '';
      api.post('/organizations/invitations', this.invite)
        .then((body) => {
          // SMTP kapalıysa posta gitmez; linki ekranda gösteriyoruz ki
          // davet yine de iletilebilsin
          this.lastInviteUrl = body.inviteUrl;
          this.notify(body.message);
          this.loadInvitations();
        })
        .catch((err) => { this.inviteError = err.message; })
        .then(() => { this.saving = false; });
    },

    revoke(invitation) {
      api.delete('/organizations/invitations/' + invitation._id)
        .then((body) => {
          this.notify(body.message);
          this.loadInvitations();
        })
        .catch((err) => { this.errorMessage = err.message; });
    },

    notify(message) {
      this.flash = message;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = ''; }, 3000);
    },

    // Ad ve e-postadan baş harf; ayrı bir avatar paketine gerek yok
    personInitials(source) {
      let text = String(source || '');
      const at = text.indexOf('@');
      if (at > 0) text = text.slice(0, at);
      const parts = text.trim().split(/[\s._-]+/).filter(Boolean);
      if (!parts.length) return '?';
      const locale = this.$locale === 'tr' ? 'tr-TR' : 'en-US';
      const take = parts.length === 1
        ? parts[0].slice(0, 2)
        : parts[0][0] + parts[parts.length - 1][0];
      return take.toLocaleUpperCase(locale);
    },
    localeLabel(locale) {
      return { tr: 'Türkçe', en: 'English', fr: 'Français' }[locale] || locale;
    },
    roleLabel(role) {
      const map = { '5': 'roleOwner', '4': 'roleAdmin', '1': 'roleMember', '0': 'roleViewer' };
      return map[role] ? this.$t('profile.' + map[role]) : role;
    },
    statusLabel(status) {
      const map = { ACTV: 'stActive', INVT: 'stInvited', PASV: 'stPassive', SUSP: 'stSuspended' };
      return map[status] ? this.$t('profile.' + map[status]) : status;
    },
    invitationLabel(invitation) {
      if (invitation.status === 'accepted') return this.$t('organization.invAccepted');
      if (invitation.status === 'revoked') return this.$t('organization.invRevoked');
      if (new Date(invitation.expiresAt) < new Date()) return this.$t('organization.invExpired');
      return this.$t('organization.invPending');
    },
    invitationPill(invitation) {
      if (invitation.status === 'accepted') return 'is-ok';
      if (invitation.status === 'revoked') return 'is-dark';
      if (new Date(invitation.expiresAt) < new Date()) return 'is-warn';
      return 'is-info';
    },
    formatDate(value) {
      return this.$formatDate(value, false);
    }
  }
};
</script>

<style scoped>
.org-page {
  max-width: 820px;
  margin: 0 auto;
}

/* --- Bildirim --- */
.flash {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
}

.flash.is-ok { background: #e9f7f2; color: #0f6e5c; }
.flash.is-err { background: #fdeceb; color: #a8322c; }

.flash-close {
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

.skeleton {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #98a0b5;
}

/* --- Banner --- */
.identity {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  margin-bottom: 18px;
  padding: 26px 24px;
  background: linear-gradient(125deg, #1d1d38 0%, #2f2f5a 55%, #3a3d75 100%);
  box-shadow: 0 10px 28px rgba(24, 24, 48, 0.22);
}

.identity::after {
  content: "";
  position: absolute;
  right: -70px;
  top: -90px;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 209, 178, 0.28) 0%, rgba(0, 209, 178, 0) 70%);
  pointer-events: none;
}

.identity-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 18px;
}

/* Kurum işareti kare: kişiler yuvarlak köşeli, kurum daha "kurumsal" */
.org-mark {
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
}

.identity-text {
  flex: 1 1 auto;
  min-width: 0;
}

.identity-name {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
}

.identity-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 5px;
}

.identity-sub code {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  padding: 1px 7px;
  font-size: 12px;
}

.sep { opacity: 0.5; margin: 0 4px; }

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 11px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.13);
  color: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 600;
}

.chip i { opacity: 0.75; }

/* --- Kart --- */
.card {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 22px 24px 24px;
  margin-bottom: 18px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.card-title {
  position: relative;
  font-size: 15px;
  font-weight: 700;
  color: #1e2233;
  padding-left: 12px;
}

.card-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;
  width: 3px;
  border-radius: 2px;
  background: #00d1b2;
}

.count {
  background: #f2f4fa;
  color: #5a6180;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 700;
}

.empty {
  color: #98a0b5;
  font-size: 14px;
  padding: 6px 0 2px;
}

/* --- Bilgi ızgarası --- */
.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  background: #eef0f6;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  overflow: hidden;
}

.fact { background: #fff; padding: 13px 15px; }
.fact.is-wide { grid-column: 1 / -1; }

.fact dt {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
  margin-bottom: 4px;
}

.fact dd {
  font-size: 14px;
  color: #2b3146;
  word-break: break-word;
}

.fact dd.is-pre { white-space: pre-line; }

/* --- Satır listesi --- */
.rows {
  border: 1px solid #eef0f6;
  border-radius: 10px;
  overflow: hidden;
}

.row {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 15px;
  background: #fff;
  border-bottom: 1px solid #f1f3f8;
}

.row:last-child { border-bottom: 0; }
.row:hover { background: #fafbfe; }

.row-mark {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(140deg, #eef1fb 0%, #e3e7f7 100%);
  color: #4a5175;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.row-mark.is-soft {
  background: #f4f6fb;
  color: #98a0b5;
  font-size: 14px;
}

.row-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.row-text strong {
  font-size: 14px;
  color: #22283a;
  font-weight: 600;
}

.row-sub {
  font-size: 12.5px;
  color: #8b93a8;
  word-break: break-word;
}

.warn { color: #c07a1f; }

.row-side {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tagline {
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
}

.pill {
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 11.5px;
  font-weight: 700;
}

.pill.is-ok { background: #e4f6ef; color: #0f6e5c; }
.pill.is-idle { background: #f2f4fa; color: #7a819a; }
.pill.is-info { background: #e8effc; color: #2d5eaa; }
.pill.is-warn { background: #fdf2e2; color: #a66a12; }
.pill.is-dark { background: #e9eaf0; color: #5a6180; }

/* --- Form --- */
.field { margin-bottom: 14px; flex: 1 1 0; min-width: 0; }

.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
  margin-bottom: 5px;
}

.field label em {
  color: #c4413c;
  font-style: normal;
}

.form-row { display: flex; gap: 12px; }

.input,
.select-input {
  width: 100%;
  height: 40px;
  border: 1px solid #dde1ea;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #2b3146;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input.is-area {
  height: auto;
  padding: 10px 12px;
  line-height: 1.6;
  resize: vertical;
}

.input:focus,
.select-input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

/* Bulma'nın .select sarmalayıcısıyla ad çakışmasın diye kendi adımız */
.select-wrap { position: relative; display: block; }

.select-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 34px;
  cursor: pointer;
}

.select-wrap::after {
  content: "";
  position: absolute;
  right: 14px;
  top: 50%;
  margin-top: -2px;
  width: 0;
  height: 0;
  border-left: 4.5px solid transparent;
  border-right: 4.5px solid transparent;
  border-top: 5px solid #8b93a8;
  pointer-events: none;
}

.hint {
  font-size: 12.5px;
  color: #98a0b5;
  margin: 2px 0 16px;
}

/* --- Düğmeler --- */
.actions { display: flex; flex-wrap: wrap; gap: 8px; }

.btn-primary,
.btn-ghost,
.btn-danger,
.btn-on-dark {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 3px 10px rgba(0, 168, 143, 0.28);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%);
}

.btn-ghost { background: #fff; border: 1px solid #dde1ea; color: #3c4257; }
.btn-ghost:hover { border-color: #00a88f; color: #0b6e5f; }

.btn-danger { background: #fff; border: 1px solid #f0cdcb; color: #b63934; }
.btn-danger:hover { background: #fdf3f2; border-color: #e0a9a5; }

.btn-on-dark {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
}

.btn-on-dark:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.45);
}

.is-mini { padding: 6px 11px; font-size: 12.5px; }

.btn-primary.is-loading { opacity: 0.7; pointer-events: none; }

/* Hatalı alan: mesaj tek başına kaybolabiliyor, çerçeve de kızarsın */
.field.has-error .input { border-color: #e0a9a5; }
.field.has-error .input:focus { box-shadow: 0 0 0 3px rgba(196, 65, 60, 0.14); }

.err {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #b63934;
  margin-top: 6px;
  line-height: 1.5;
}

.btn-primary:disabled {
  background: #e7eaf2;
  color: #a7aec1;
  box-shadow: none;
  cursor: not-allowed;
}

/* --- API anahtarları --- */
.copy-line {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.copy-line code {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-btn {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #8b93a8;
  font-size: 11px;
  cursor: pointer;
}

.copy-btn:hover { border-color: #00b89c; color: #0b6e5f; background: #f2fcfa; }

.warn-inline {
  display: inline-block;
  margin-left: 6px;
  color: #a66a12;
}

.facts.mt { margin-top: 14px; }

.fact code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  color: #4a5175;
  background: #f4f6fb;
  border-radius: 5px;
  padding: 2px 7px;
  word-break: break-all;
}

.row-sub code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11.5px;
  color: #5a6180;
  background: #f4f6fb;
  border-radius: 4px;
  padding: 1px 6px;
}

.hint {
  font-size: 12.5px;
  color: #98a0b5;
  line-height: 1.6;
  margin-bottom: 12px;
}

/* Segment: geçerlilik süresi seçimi */
.seg {
  display: flex;
  background: #f2f4fa;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.seg button {
  flex: 1 1 0;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
}

.seg button.is-active {
  background: #fff;
  color: #0b6e5f;
  box-shadow: 0 1px 3px rgba(24, 24, 48, 0.14);
}

/* --- Davet penceresi --- */
/* Bulma'nın modal'ı yerine kendi kabuğumuz: başlıkta ikon, altta
   ayrılmış eylem çubuğu, arkada bulanık zemin */
.sheet {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(20, 20, 38, 0.55);
  backdrop-filter: blur(3px);
}

.sheet-card {
  position: relative;
  width: 480px;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(16, 16, 36, 0.35);
  overflow: hidden;
  animation: sheet-in 0.18s ease-out;
}

@keyframes sheet-in {
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to { opacity: 1; transform: none; }
}

.sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #eef0f6;
}

.sheet-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%);
  color: #06302a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.sheet-head-text { flex: 1 1 auto; min-width: 0; }

.sheet-head-text h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1e2233;
  line-height: 1.3;
}

.sheet-head-text p {
  font-size: 12.5px;
  color: #8b93a8;
  margin-top: 3px;
  line-height: 1.5;
}

.sheet-close {
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  font-size: 24px;
  line-height: 1;
  color: #b3bacb;
  cursor: pointer;
  padding: 0 2px;
}

.sheet-close:hover { color: #6b7283; }

.sheet-body {
  padding: 18px 20px 4px;
  overflow-y: auto;
}

.sheet-foot {
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eef0f6;
  background: #fbfcfe;
}

/* --- Segment --- */
.seg {
  display: flex;
  background: #f2f4fa;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.seg button {
  flex: 1 1 0;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.seg button:hover { color: #4a5175; }

.seg button.is-active {
  background: #fff;
  color: #0b6e5f;
  box-shadow: 0 1px 3px rgba(24, 24, 48, 0.14);
}

/* --- Gönderim sonrası --- */
.sent-box { padding: 4px 0 8px; }

.sent-title {
  font-size: 14.5px;
  font-weight: 600;
  color: #1e2233;
  margin-bottom: 12px;
}

.link-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f4f7fb;
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  padding: 10px 10px 10px 13px;
}

.link-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12.5px;
  color: #4a5175;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-row .btn-ghost { flex: 0 0 auto; }

@media screen and (max-width: 640px) {
  .sheet { padding: 0; align-items: flex-end; }
  .sheet-card { width: 100%; border-radius: 16px 16px 0 0; max-height: 92vh; }
  .facts { grid-template-columns: 1fr; }
  .form-row { flex-direction: column; gap: 0; }
  .identity-main { flex-direction: column; align-items: flex-start; gap: 14px; }
  .identity { padding: 22px 18px; }
  .row { flex-wrap: wrap; }
  .row-side { width: 100%; padding-left: 51px; }
}
</style>


// WEBPACK FOOTER //
// src/Components/Organization.vue